"use client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Ghost } from "lucide-react";
import { Ticket } from "./ticket";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function EventTickets({
  tickets,
  remainingQuantity,
  setAttendeesInfos,
  updateCartCallback,
  setAccessCode,
  accessCode,
  cart,
  hiddenTickets,
  refreshTickets,
  setRefreshTickets,
  answers,
  setAnswers,
  attendeeCopiedInfos,
  canChangeQuantity,
  hidePrice,
  isCheckoutPage = false,
  groupByCategory = false, // Prop to enable/disable category grouping
}: any) {
  const t = useTranslations("Tickets");

  const [jiggle, setJiggle] = useState(false);
  const [parentChildDistribution, setParentChildDistribution] = useState<any>(
    {},
  );

  useEffect(() => {
    if (setAccessCode) {
      const timer = setTimeout(() => {
        setJiggle(false);
        setAccessCode("");
      }, 1000);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jiggle]);

  useEffect(() => {
    window.addEventListener("wrong-access-code", () => {
      setJiggle(true);
    });

    return () => {
      window.removeEventListener("wrong-access-code", () => setJiggle(true));
    };
  }, []);

  // Calculate distribution of child tickets across parent instances when cart changes
  useEffect(() => {
    if (isCheckoutPage && cart && tickets) {
      const newDistribution = calculateParentChildDistribution(tickets, cart);
      setParentChildDistribution(newDistribution);
    }
  }, [cart, tickets, isCheckoutPage]);

  // Add this useEffect to listen for changes to answers
  useEffect(() => {
    if (
      isCheckoutPage &&
      answers?.length > 0 &&
      Object.keys(parentChildDistribution).length > 0
    ) {
      const updatedAnswers = [...answers];

      // Go through all parent ticket types
      Object.keys(parentChildDistribution).forEach((parentTicketId) => {
        // Go through all instances of this parent ticket
        parentChildDistribution[parentTicketId].instances.forEach(
          (instance: any, instanceIndex: any) => {
            // Find the answers for this parent instance
            const parentInstanceId = `parent-${parentTicketId}-${instanceIndex}`;
            const parentAnswersObj = updatedAnswers.find(
              (a) => a.ticketIndex === parentInstanceId,
            );

            if (parentAnswersObj) {
              // Go through all child ticket types for this parent instance
              Object.keys(instance).forEach((childTicketId) => {
                const childQuantity = instance[childTicketId];

                // For each child ticket instance
                for (let i = 0; i < childQuantity; i++) {
                  const childInstanceId = `child-${childTicketId}-${parentTicketId}-${instanceIndex}-${i}`;

                  // Check if this child already has answers
                  const existingChildIndex = updatedAnswers.findIndex(
                    (a) => a.ticketIndex === childInstanceId,
                  );

                  // Create or update child answers based on parent
                  const childAnswers = {
                    ticketIndex: childInstanceId,
                    ticketId: childTicketId,
                    fullName: parentAnswersObj.fullName,
                    email: parentAnswersObj.email,
                    phoneNumber: parentAnswersObj.phoneNumber,
                    additionalAnswers: [
                      ...(parentAnswersObj.additionalAnswers || []),
                    ],
                    inheritedFromParent: parentInstanceId,
                  };

                  if (existingChildIndex >= 0) {
                    // Update existing entry
                    updatedAnswers[existingChildIndex] = childAnswers;
                  } else {
                    // Add new entry
                    updatedAnswers.push(childAnswers);
                  }
                }
              });
            }
          },
        );
      });

      // Update answers state if there are changes
      if (JSON.stringify(updatedAnswers) !== JSON.stringify(answers)) {
        setAnswers(updatedAnswers);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, parentChildDistribution, isCheckoutPage]);

  // Calculate how child tickets should be distributed across parent instances
  const calculateParentChildDistribution = (tickets: any, cart: any) => {
    const distribution: any = {};

    if (!tickets || !cart) return distribution;

    // Group tickets by ID to handle duplicates
    const ticketsById = tickets.reduce((acc: any, ticket: any) => {
      const id = ticket.id;
      if (!acc[id]) {
        acc[id] = ticket;
      }
      return acc;
    }, {});

    // Get all parent tickets with quantities
    const parentTicketsWithQuantity = Object.values(ticketsById)
      .filter((ticket: any) => !ticket.parentTicketId && cart[ticket.id] > 0)
      .map((ticket: any) => ({
        ticket,
        quantity: cart[ticket.id],
      }));

    // Get all child tickets with quantities
    const childTicketsWithQuantity = Object.values(ticketsById)
      .filter((ticket: any) => ticket.parentTicketId && cart[ticket.id] > 0)
      .map((ticket: any) => ({
        ticket,
        quantity: cart[ticket.id],
        parentId: ticket.parentTicketId,
      }));

    // For each parent ticket type
    parentTicketsWithQuantity.forEach(
      ({
        ticket: parentTicket,
        quantity: parentQuantity,
      }: {
        ticket: any;
        quantity: number;
      }) => {
        const parentId = parentTicket.id;

        // Find children for this parent
        const relevantChildren = childTicketsWithQuantity.filter(
          ({ ticket }: { ticket: any }) => ticket.parentTicketId === parentId,
        );

        // Initialize distribution for this parent
        if (!distribution[parentId]) {
          distribution[parentId] = {
            instances: Array.from({ length: parentQuantity }, () => ({})),
          };
        }

        // For each child ticket type that belongs to this parent
        relevantChildren.forEach(
          ({
            ticket: childTicket,
            quantity: childQuantity,
          }: {
            ticket: any;
            quantity: number;
          }) => {
            const childId = childTicket.id;

            // Distribute children using round robin
            let remainingToDistribute = childQuantity;
            let instanceIdx = 0;

            while (remainingToDistribute > 0 && instanceIdx < parentQuantity) {
              // Initialize child quantity for this parent instance if not exists
              if (!distribution[parentId].instances[instanceIdx][childId]) {
                distribution[parentId].instances[instanceIdx][childId] = 0;
              }

              // Add one child to this parent instance
              distribution[parentId].instances[instanceIdx][childId]++;
              remainingToDistribute--;
              instanceIdx = (instanceIdx + 1) % parentQuantity;
            }
          },
        );
      },
    );

    return distribution;
  };

  // Separate parent and child tickets
  const parentTickets = tickets?.filter(
    (ticket: any) => !ticket.parentTicketId,
  );

  // Find child tickets for a specific parent
  const getChildTickets = (parentId: number) => {
    return (
      tickets?.filter((ticket: any) => ticket.parentTicketId === parentId) || []
    );
  };

  // Get quantity of a specific ticket in cart
  const getQuantityInCart = (ticketId: number) => {
    return cart && cart[ticketId] ? cart[ticketId] : 0;
  };

  // Group tickets by category if enabled
  const getTicketsByCategory = () => {
    if (!parentTickets || !groupByCategory) return {};

    return parentTickets.reduce((acc: any, ticket: any) => {
      const category = ticket.category || t("uncategorized");
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(ticket);
      return acc;
    }, {});
  };

  const ticketsByCategory = getTicketsByCategory();
  const categories = Object.keys(ticketsByCategory);

  // Extract uncategorized tickets
  const uncategorizedTickets =
    groupByCategory && ticketsByCategory[t("uncategorized")]
      ? ticketsByCategory[t("uncategorized")]
      : [];

  // Filter categories to exclude uncategorized and sort alphabetically
  const categoriesWithoutUncategorized = categories
    .filter((category) => category !== t("uncategorized"))
    .sort((a, b) => a.localeCompare(b));

  // Function to render a single ticket
  const renderTicket = (ticket: any, index: any, isUncategorized = false) => {
    // Find the global index for this ticket
    const globalIndex = isUncategorized
      ? parentTickets.findIndex((t: any) => t.id === ticket.id)
      : index;

    return (
      <Ticket
        key={ticket.id + "-" + index}
        ticket={ticket}
        remainingQuantity={
          remainingQuantity &&
          remainingQuantity.length > -1 &&
          remainingQuantity.filter((qty: any) => qty.ticketId == ticket.id)[0]
            ?.remaining
        }
        updateCartCallback={updateCartCallback}
        quantityInCart={cart ? cart[ticket.id] : 0}
        answers={answers}
        setAnswers={setAnswers}
        setAttendeesInfos={setAttendeesInfos}
        ticketIndex={globalIndex}
        attendeeCopiedInfos={
          globalIndex === 0 ? attendeeCopiedInfos : undefined
        }
        canChangeQuantity={canChangeQuantity}
        hidePrice={hidePrice}
      />
    );
  };

  // Add translation keys for child ticket inheritance
  useEffect(() => {
    // This is just a placeholder to remind you to add the translation keys
    // You should add these keys to your actual translation files:
    //
    // "General": {
    //   "childTicketInheritInfo": "Information for this ticket will be automatically copied from the parent ticket.",
    //   "inheritedFromParent": "Value inherited from parent ticket",
    //   "no-file-selected": "No file selected"
    // }
  }, []);

  const renderCheckoutVersion = () => {
    let globalIndex = 0; // Initialize counter for render order

    return (
      <>
        {Object.keys(parentChildDistribution).map((parentId) => {
          const parentTicket = tickets.find(
            (ticket: any) => ticket.id === parentId,
          );
          if (!parentTicket) return null;

          const distribution = parentChildDistribution[parentId];
          // Map through the instances array from the distribution object
          return distribution.instances.map(
            (childDistribution: any, instanceIdx: number) => (
              <div
                key={`${parentId}-instance-${instanceIdx}`}
                className="mb-8 border-b pb-4"
              >
                <h3 className="font-medium mb-2">{`${parentTicket.name} - Ticket ${instanceIdx + 1}`}</h3>

                {/* Parent Ticket Instance */}
                <Ticket
                  key={`${parentId}-${instanceIdx}`}
                  ticket={parentTicket}
                  remainingQuantity={remainingQuantity}
                  updateCartCallback={updateCartCallback}
                  quantityInCart={1} // Each instance is 1 ticket
                  answers={answers}
                  setAnswers={setAnswers}
                  setAttendeesInfos={setAttendeesInfos}
                  ticketIndex={`parent-${parentId}-${instanceIdx}`}
                  attendeeCopiedInfos={attendeeCopiedInfos}
                  canChangeQuantity={false}
                  hidePrice={hidePrice}
                  isCheckoutInstance={true}
                  instanceIndex={instanceIdx}
                  ticketGlobalIndex={globalIndex++} // Assign and increment
                />

                {/* Child Tickets for this specific parent instance */}
                <div className="pl-6 mt-2 mb-4 border-l-2 border-gray-200">
                  {Object.keys(childDistribution).map((childId) => {
                    const childTicket = tickets.find(
                      (ticket: any) => ticket.id === childId,
                    );
                    if (!childTicket) return null;

                    const childQuantity = childDistribution[childId];
                    if (childQuantity <= 0) return null;

                    return (
                      <Ticket
                        key={`${childId}-parent-${instanceIdx}`}
                        ticket={childTicket}
                        remainingQuantity={remainingQuantity}
                        updateCartCallback={updateCartCallback}
                        quantityInCart={childQuantity}
                        answers={answers}
                        setAnswers={setAnswers}
                        setAttendeesInfos={setAttendeesInfos}
                        ticketIndex={`child-${childId}-${instanceIdx}`}
                        canChangeQuantity={false}
                        hidePrice={hidePrice}
                        isChildTicket={true}
                        isCheckoutInstance={true}
                        instanceIndex={instanceIdx}
                        parentInstanceIndex={instanceIdx}
                        ticketGlobalIndex={globalIndex++} // Assign and increment
                      />
                    );
                  })}
                </div>
              </div>
            ),
          );
        })}
      </>
    );
  };

  // Function to render tickets without category grouping (original way)
  const renderRegularSelectionVersion = () => {
    return (
      <>
        {parentTickets?.map((parentTicket: any, parentIndex: any) => {
          const parentQuantityInCart = getQuantityInCart(parentTicket.id);
          const childTickets = getChildTickets(parentTicket.id);

          return (
            <div key={parentTicket.id + "-container"}>
              {/* Parent Ticket */}
              <Ticket
                key={parentTicket.id}
                ticket={parentTicket}
                remainingQuantity={
                  remainingQuantity &&
                  remainingQuantity.length > -1 &&
                  remainingQuantity.filter(
                    (qty: any) => qty.ticketId == parentTicket.id,
                  )[0]?.remaining
                }
                updateCartCallback={updateCartCallback}
                quantityInCart={parentQuantityInCart}
                answers={answers}
                setAnswers={setAnswers}
                setAttendeesInfos={setAttendeesInfos}
                ticketIndex={parentIndex}
                attendeeCopiedInfos={
                  parentIndex === 0 ? attendeeCopiedInfos : undefined
                }
                canChangeQuantity={canChangeQuantity}
                hidePrice={hidePrice}
              />

              {/* Child Tickets - Only show if parent is selected */}
              {parentQuantityInCart > 0 && childTickets.length > 0 && (
                <div className="pl-6 mt-2 mb-4 border-l-2 border-gray-200">
                  {childTickets.map((childTicket: any, childIndex: any) => (
                    <Ticket
                      key={childTicket.id}
                      ticket={childTicket}
                      remainingQuantity={
                        remainingQuantity &&
                        remainingQuantity.length > -1 &&
                        remainingQuantity.filter(
                          (qty: any) => qty.ticketId == childTicket.id,
                        )[0]?.remaining
                      }
                      updateCartCallback={updateCartCallback}
                      quantityInCart={getQuantityInCart(childTicket.id)}
                      answers={answers}
                      setAnswers={setAnswers}
                      setAttendeesInfos={setAttendeesInfos}
                      ticketIndex={parentTickets?.length + childIndex}
                      canChangeQuantity={canChangeQuantity}
                      hidePrice={hidePrice}
                      isChildTicket={true}
                      parentTotalQuantity={parentQuantityInCart}
                      parentInstanceIndex={parentIndex}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </>
    );
  };

  // Function to render tickets grouped by category (excluding uncategorized)
  const renderCategorizedSelectionVersion = () => {
    return (
      <>
        {/* Render uncategorized tickets independently (outside of accordion) */}
        {uncategorizedTickets.map((ticket: any, index: any) => {
          const parentQuantityInCart = getQuantityInCart(ticket.id);
          const childTickets = getChildTickets(ticket.id);
          const globalIndex = parentTickets.findIndex(
            (t: any) => t.id === ticket.id,
          );

          return (
            <div key={ticket.id + "-container"}>
              {/* Parent Ticket */}
              <Ticket
                key={ticket.id}
                ticket={ticket}
                remainingQuantity={
                  remainingQuantity &&
                  remainingQuantity.length > -1 &&
                  remainingQuantity.filter(
                    (qty: any) => qty.ticketId == ticket.id,
                  )[0]?.remaining
                }
                updateCartCallback={updateCartCallback}
                quantityInCart={parentQuantityInCart}
                answers={answers}
                setAnswers={setAnswers}
                setAttendeesInfos={setAttendeesInfos}
                ticketIndex={globalIndex}
                attendeeCopiedInfos={
                  globalIndex === 0 ? attendeeCopiedInfos : undefined
                }
                canChangeQuantity={canChangeQuantity}
                hidePrice={hidePrice}
              />

              {/* Child Tickets - Only show if parent is selected */}
              {parentQuantityInCart > 0 && childTickets.length > 0 && (
                <div className="pl-6 mt-2 mb-4 border-l-2 border-gray-200">
                  {childTickets.map((childTicket: any, childIndex: any) => (
                    <Ticket
                      key={childTicket.id}
                      ticket={childTicket}
                      remainingQuantity={
                        remainingQuantity &&
                        remainingQuantity.length > -1 &&
                        remainingQuantity.filter(
                          (qty: any) => qty.ticketId == childTicket.id,
                        )[0]?.remaining
                      }
                      updateCartCallback={updateCartCallback}
                      quantityInCart={getQuantityInCart(childTicket.id)}
                      answers={answers}
                      setAnswers={setAnswers}
                      setAttendeesInfos={setAttendeesInfos}
                      ticketIndex={parentTickets?.length + childIndex}
                      canChangeQuantity={canChangeQuantity}
                      hidePrice={hidePrice}
                      isChildTicket={true}
                      parentTotalQuantity={parentQuantityInCart}
                      parentInstanceIndex={globalIndex}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Only render accordion if there are categorized tickets */}
        {categoriesWithoutUncategorized.length > 0 && (
          <Accordion type="multiple" className="w-full mt-4">
            {categoriesWithoutUncategorized.map((category) => (
              <AccordionItem key={category} value={category}>
                <AccordionTrigger className="font-medium text-lg">
                  {category}
                </AccordionTrigger>
                <AccordionContent>
                  {ticketsByCategory[category].map((ticket: any) => {
                    const parentQuantityInCart = getQuantityInCart(ticket.id);
                    const childTickets = getChildTickets(ticket.id);
                    const globalIndex = parentTickets.findIndex(
                      (t: any) => t.id === ticket.id,
                    );

                    return (
                      <div key={ticket.id + "-container"}>
                        {/* Parent Ticket */}
                        <Ticket
                          key={ticket.id}
                          ticket={ticket}
                          remainingQuantity={
                            remainingQuantity &&
                            remainingQuantity.length > -1 &&
                            remainingQuantity.filter(
                              (qty: any) => qty.ticketId == ticket.id,
                            )[0]?.remaining
                          }
                          updateCartCallback={updateCartCallback}
                          quantityInCart={parentQuantityInCart}
                          answers={answers}
                          setAnswers={setAnswers}
                          setAttendeesInfos={setAttendeesInfos}
                          ticketIndex={globalIndex}
                          attendeeCopiedInfos={
                            globalIndex === 0 ? attendeeCopiedInfos : undefined
                          }
                          canChangeQuantity={canChangeQuantity}
                          hidePrice={hidePrice}
                        />

                        {/* Child Tickets - Only show if parent is selected */}
                        {parentQuantityInCart > 0 &&
                          childTickets.length > 0 && (
                            <div className="pl-6 mt-2 mb-4 border-l-2 border-gray-200">
                              {childTickets.map(
                                (childTicket: any, childIndex: any) => (
                                  <Ticket
                                    key={childTicket.id}
                                    ticket={childTicket}
                                    remainingQuantity={
                                      remainingQuantity &&
                                      remainingQuantity.length > -1 &&
                                      remainingQuantity.filter(
                                        (qty: any) =>
                                          qty.ticketId == childTicket.id,
                                      )[0]?.remaining
                                    }
                                    updateCartCallback={updateCartCallback}
                                    quantityInCart={getQuantityInCart(
                                      childTicket.id,
                                    )}
                                    answers={answers}
                                    setAnswers={setAnswers}
                                    setAttendeesInfos={setAttendeesInfos}
                                    ticketIndex={
                                      parentTickets?.length + childIndex
                                    }
                                    canChangeQuantity={canChangeQuantity}
                                    hidePrice={hidePrice}
                                    isChildTicket={true}
                                    parentTotalQuantity={parentQuantityInCart}
                                    parentInstanceIndex={globalIndex}
                                  />
                                ),
                              )}
                            </div>
                          )}
                      </div>
                    );
                  })}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </>
    );
  };

  // Render selection version (for ticket selection phase) - dispatches to appropriate rendering function
  const renderSelectionVersion = () => {
    return groupByCategory
      ? renderCategorizedSelectionVersion()
      : renderRegularSelectionVersion();
  };

  return (
    <>
      {tickets && tickets.length === 0 && !hiddenTickets && (
        <Alert variant="destructive">
          <AlertTitle>{t("noTicketsAlert.title")}</AlertTitle>
          <AlertDescription>{t("noTicketsAlert.description")}</AlertDescription>
        </Alert>
      )}

      {/* Render different versions based on page type */}
      {isCheckoutPage ? renderCheckoutVersion() : renderSelectionVersion()}

      {hiddenTickets && (
        <Alert
          className={cn(
            "flex flex-col border-0 bg-background/50 content-stretch",
            jiggle && "animate-jiggle",
          )}
        >
          <Ghost className="w-4 h-4" />
          <AlertTitle className="text-sm">
            {t("hiddenTicketsAlert.title")}
          </AlertTitle>
          <AlertDescription className="flex text-xs text-muted-foreground">
            <div className="hidden grow md:block">
              {t("hiddenTicketsAlert.description")}
            </div>
            <div className="flex items-center justify-between w-54">
              <div className="flex items-center w-full space-x-2">
                <Input
                  type="text"
                  placeholder={t("hiddenTicketsAlert.inputPlaceholder")}
                  className="w-32"
                  onChange={(e) =>
                    setAccessCode(
                      e.target.value.toUpperCase().replaceAll(" ", "_"),
                    )
                  }
                  value={accessCode}
                />
                <Button
                  disabled={!accessCode}
                  type="submit"
                  onClick={() => setRefreshTickets(!refreshTickets)}
                >
                  {t("hiddenTicketsAlert.unlockButton")}
                </Button>
              </div>
            </div>
          </AlertDescription>
        </Alert>
      )}
    </>
  );
}
